import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, repeatsAClass } from '../skillUsage';

export const slatWipeRevealSkill: CodeoSkill = {
    name: SKILL_NAMES.slatWipeReveal,
    kind: 'codeo',
    description:
        'Reveal or clear the frame through a row of staggered slats, like a blind opening — a ' +
        'mechanical scene change with more grain than a plain wipe.',
    documentUsesSkill: check((script, markup) => /scaleY/.test(script) && repeatsAClass(markup)),
    instructions: `# Slat Wipe Reveal

The frame is divided into vertical slats that clear one after another, exposing what is behind them. The stagger is what distinguishes it from a wipe: a wipe is a single edge travelling, while slats read as a mechanism operating — a blind opening, a board flipping over.

Use it to open a scene, to clear a cover off content that is already in place, or to close on a state you want to feel deliberately shut away.

## Markup

Author the slats as siblings in a flex row so they tile the frame exactly with no seams. The content sits underneath them.

\`\`\`html
<div id="slat-stage" class="overflow-hidden" style="position: absolute; inset: 0;">
  <div id="revealed" class="flex items-center justify-center" style="position: absolute; inset: 0; font-family: var(--l5-font-0), sans-serif; font-size: 132px; font-weight: 800; color: var(--l5-color-0, #101820);">Now shipping</div>
  <div id="slat-cover" class="flex" data-l5-allow-overlap style="position: absolute; inset: 0;">
    <div class="slat flex-1" style="background: var(--l5-color-1, #101820)"></div>
    <div class="slat flex-1" style="background: var(--l5-color-1, #101820)"></div>
    <div class="slat flex-1" style="background: var(--l5-color-1, #101820)"></div>
    <div class="slat flex-1" style="background: var(--l5-color-1, #101820)"></div>
    <div class="slat flex-1" style="background: var(--l5-color-1, #101820)"></div>
    <div class="slat flex-1" style="background: var(--l5-color-1, #101820)"></div>
  </div>
</div>
\`\`\`

Give the slats a 1px negative margin overlap if a hairline seam shows between them after rasterizing.

## Timeline code

\`scaleY\` from a bottom origin makes each slat retract rather than slide, which keeps the mechanism reading as fixed in place.

\`\`\`js
const tl = gsap.timeline({ paused: true });

gsap.set('.slat', { scaleY: 1, transformOrigin: 'bottom center' });

tl.to('.slat', {
    scaleY: 0,
    duration: 0.62,
    ease: 'power3.inOut',
    stagger: { each: 0.07, from: 'start' },
}, 0.3);

window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Direction**: \`from: 'end'\` runs right to left, \`from: 'center'\` opens outward from the middle, \`from: 'edges'\` closes toward it. Centre-out is the most confident; edges-in suits a close.
- **Horizontal slats**: stack rows with \`flex-direction: column\` and tween \`scaleX\` from a \`left center\` origin.
- **Slat count**: 6–10 for a mechanism you can read. Above about 16 it becomes a dissolve; at 3 it is a chunky wipe.
- **Overlap**: raise \`each\` to 0.12 for a slow, deliberate opening; drop to 0.03 and the slats read as a single soft edge.
- **Cover instead of reveal**: run it in reverse (\`scaleY: 0\` → \`1\`) to shut the frame before a cut.
- **Alternating**: give even slats a \`top center\` origin and odd ones \`bottom center\` for a woven look — worth it only when the slats are the design, not just the transition.

## Pitfalls

- The slats are \`scaleY\`-tweened, so none may carry a CSS \`transform\`. Set \`transformOrigin\` through \`gsap.set\`, as above, rather than in CSS.
- \`flex: 1 1 0\` on every slat is what makes them tile exactly; percentage widths accumulate rounding error and leave a gap at one edge.
- The revealed content must already be laid out underneath — don't also animate it in, or the reveal and the entrance fight.
- Keep the whole wipe under about 1s. A slow mechanism reads as a loading state.
- Don't slat-wipe into a state that then holds still. The wipe is the entrance; the scene still needs its own life afterwards.
- Six full-height elements scaling at once is cheap, but don't give each slat a blur or shadow — that is where this gets expensive to rasterize.
- Alternating with a second transition technique in the same graphic reads as indecision. Pick one scene-change vocabulary — see \`${SKILL_NAMES.momentSweepTransition}\` for the single-edge alternative.`,
};
